import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

import Loading from '../components/loading';

import './home.css';
import { useNavigate } from 'react-router-dom';
import { IMAGES } from '../utils/images';
import { useAudio } from '../audio/AudioProvider';
import { useAuth } from '../AuthProvider';
import { normalizeAudioSettingLevel0to100 } from '../utils/applyAudioSettingsPatch';

import Send from '../assets/icons/Send.svg?react';
import Music from '../assets/icons/Music.svg?react';
import NoMusic from '../assets/icons/NoMusic.svg?react';
import RoomService from '../services/room.service';

/** Music level when unmuting from the home header icon (0 → this value). */
const HOME_MUSIC_ICON_UNMUTE_LEVEL = 50;

/** Current 0–100 music level from settings (`undefined` / invalid → 100). */
function homePageMusicLevel(settingsMusic: unknown): number {
    return normalizeAudioSettingLevel0to100(settingsMusic);
}

/** Home music icon: if audible, mute (0); if muted, go to {@link HOME_MUSIC_ICON_UNMUTE_LEVEL}. */
function nextHomePageMusicLevelAfterIconClick(currentLevel: number): number {
    return currentLevel > 0 ? 0 : HOME_MUSIC_ICON_UNMUTE_LEVEL;
}

const HomePage: React.FC = () => {
    const { user, logout, patchUserSettings } = useAuth();
    const { playerHover } = useAudio();

    const [showPlay, setShowPlay] = useState(false);
    const [showProfile, setShowProfile] = useState(false);

    const [width, setWidth] = useState(window.innerWidth);
    const [height, setHeight] = useState(window.innerHeight);

    const navigate = useNavigate();

    const [imagesLoading, setImagesLoading] = useState(true);
    const [isCreatingRoom, setIsCreatingRoom] = useState(false);

    const images = IMAGES.home;
    const numLoadedRef = useRef(0);
    const numImages = Object.keys(images).length;

    useEffect(() => {
        numLoadedRef.current = 0;

        const handleWindowResize = () => {
            setWidth(window.innerWidth);
            setHeight(window.innerHeight);
        };
        window.addEventListener("resize", handleWindowResize);

        const handleUnload = () => {
            console.log("Unload");
            window.removeEventListener("resize", handleWindowResize);
        };
        window.addEventListener('beforeunload', handleUnload);

        return () => {
            window.removeEventListener("beforeunload", handleUnload);
            window.removeEventListener("resize", handleWindowResize);
        };
    }, []);

    const musicLevel = homePageMusicLevel(user.settings?.music);
    const musicAudible = musicLevel > 0;

    const onMusicToggle = useCallback(async () => {
        const next = nextHomePageMusicLevelAfterIconClick(musicLevel);
        try {
            await patchUserSettings({ music: next });
        } catch {
            alert('Could not save music setting. Please try again.');
        }
    }, [musicLevel, patchUserSettings]);

    function checkImages() {
        numLoadedRef.current += 1;
        if (numLoadedRef.current >= numImages) {
            setImagesLoading(false);
        }
    }

    function joinRoom(roomName: string) {
        if (!roomName.trim()) {
            alert('Please enter a room code');
            return;
        }
        // Navigate to GamePage - the route should be /game/:roomCode or /:roomCode
        // Adjust based on your routing setup
        navigate(`/${roomName}`);
    }

    async function createRoom(botGame: boolean) {
        try {
            setIsCreatingRoom(true);
            
            // Create room via API - backend will generate unique room code
            const data = {
                botGame: botGame,
                gameType: "CASUAL" as const,
                theme: "ocean",
                privateRoom: false,
            };
            
            const response = await RoomService.create(data);
            
            // Extract room code from response
            // Response type ensures code is always present on success
            const roomCode = response.data.code;
            
            if (!roomCode) {
                throw new Error("Room created but no room code returned");
            }
            
            // Navigate to GamePage - it will handle joining via socket
            navigate(`/${roomCode}`);
        } catch (e: any) {
            setIsCreatingRoom(false);
            const errorMessage = e?.response?.data?.error || e?.message || "Failed to create room";
            alert(errorMessage);
            console.error("Error creating room: ", e);
        }
    }

    function handlePlay() {
        setShowPlay(true);
    }

    function handleProfileClick() {
        setShowProfile((open) => !open);
    }

    async function handleLogout() {
        if (user?.guest) return;
        await logout();
        navigate('/login');
    }

    return ReactDOM.createPortal(
        <div className="main_container">
            <div className="home_top_left">
                <button
                    type="button"
                    className="home_music_toggle"
                    onClick={onMusicToggle}
                    onPointerEnter={() => playerHover()}
                    aria-label={musicAudible ? 'Mute music' : 'Turn music on (50%)'}
                    aria-pressed={musicAudible}
                >
                    {musicAudible ? (
                        <Music className="home_music_icon" aria-hidden />
                    ) : (
                        <NoMusic className="home_music_icon" aria-hidden />
                    )}
                </button>
            </div>
            {(imagesLoading || isCreatingRoom) && <Loading text={isCreatingRoom ? "Creating room..." : "Loading"} />}
            <img
                className="front_waves"
                src={images['front_water.png']}
                onLoad={checkImages}
            />
            <div className='monster_container'>
                <img
                    className="monster"
                    src={images['sea_monster.png']}
                    onLoad={checkImages}
                />
            </div>

            <img
                className="mountain"
                src={images['mountain.png']}
                onLoad={checkImages}
            />
            <img
                className="ship"
                src={images['ship.png']}
                onLoad={checkImages}
            />
            <img
                className="right_waves"
                src={images['blue_background_waves.png']}
                onLoad={checkImages}
            />
            <img
                className="blue_purple"
                src={images['navy_purple.png']}
                onLoad={checkImages}
            />

            <img
                className="side_waves"
                src={images['side_waves.png']}
                onLoad={checkImages}
            />
            <img
                className="cream"
                src={images['cream.png']}
                onLoad={checkImages}
            />
            <img
                className="title"
                src={images['title.png']}
                onLoad={checkImages}
            />

            <img
                className="particles"
                src={images['particles.png']}
                onLoad={checkImages}
            />

            <div
                className="side_bar"
                style={{ right: 0 }}
            />

            <div
                className="side_bar"
                style={{ left: 0 }}
            />

            <div className="bottom_buttons">

                {(width / height) > (80 / 55) &&
                    <div className="button_container">
                        <button
                            type="button"
                            className="button"
                            onClick={() => navigate('/tutorial')}
                            onPointerEnter={() => playerHover()}
                        >
                            Tutorial
                        </button>
                    </div>
                }
                <div className="button_container">
                    <button
                        type="button"
                        className="button"
                        onClick={handlePlay}
                        onPointerEnter={() => playerHover()}
                    >
                        Play
                    </button>
                </div>

                {(width / height) <= (80 / 55) &&
                    <div className="button_container">
                        <button
                            type="button"
                            className="button"
                            onClick={() => navigate('/tutorial')}
                            onPointerEnter={() => playerHover()}
                        >
                            Tutorial
                        </button>
                    </div>
                }
                <div className="button_container">
                    <button
                        type="button"
                        className="button"
                        onClick={() => navigate('/credits')}
                        onPointerEnter={() => playerHover()}
                    >
                        Credits
                    </button>
                </div>
            </div>
            {showPlay && <PlayOptions closeModal={setShowPlay} createRoom={createRoom} joinRoom={joinRoom} />}
            <div className='home_top_right'>
                <p className='home_top_name'>{user.username}</p>
                <div className="home_profile_menu">
                    <div className="profile_icon_container">
                        <button
                            type="button"
                            className="home_profile_avatar"
                            onClick={handleProfileClick}
                            onPointerEnter={() => playerHover()}
                            aria-label="Account menu"
                            aria-expanded={showProfile}
                            aria-haspopup="true"
                        >
                            <img
                                src={IMAGES.profile_pics['default.png']}
                                alt=""
                                className="home_profile_pic"
                            />
                        </button>
                    </div>
                    {showProfile && (
                        <div
                            className="profile_options"
                            role="menu"
                            onMouseLeave={() => setShowProfile(false)}
                        >
                            {user?.guest ? (
                                <>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        className="profile_option"
                                        onClick={() => {
                                            setShowProfile(false);
                                            navigate('/profile');
                                        }}
                                    >
                                        Settings
                                    </button>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        className="profile_option"
                                        onClick={() => {
                                            setShowProfile(false);
                                            navigate('/login');
                                        }}
                                    >
                                        Log in
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        role="menuitem"
                                        className="profile_option"
                                        onClick={() => {
                                            setShowProfile(false);
                                            navigate('/profile');
                                        }}
                                    >
                                        Profile
                                    </button>
                                    {user.role === 'ADMIN' && (
                                        <button
                                            type="button"
                                            role="menuitem"
                                            className="profile_option"
                                            onClick={() => {
                                                setShowProfile(false);
                                                navigate('/admin');
                                            }}
                                        >
                                            Admin
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        role="menuitem"
                                        className="profile_option profile_option--danger"
                                        onClick={handleLogout}
                                    >
                                        Logout
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

        </div>,
        document.body
    );
};

export default HomePage;

interface PlayOptionsProps {
    closeModal: (value: boolean) => void;
    createRoom: (botGame: boolean) => void;
    joinRoom: (roomName: string) => void;
}

export const PlayOptions: React.FC<PlayOptionsProps> = ({ closeModal, createRoom, joinRoom }) => {
    const { playerHover } = useAudio();
    const [roomName, setRoomName] = useState("");

    const [enterCode, setEnterCode] = useState(false);

    function handleOutsideClick(event: React.MouseEvent<HTMLDivElement>) {
        if (event.target instanceof HTMLElement && event.target.id && event.target.id === "outside") {
            closeModal(false);
        }
    }

    function handleJoinRoom(e: React.FormEvent) {
        e.preventDefault();
        joinRoom(roomName);
    }

    return (
        <div id='outside' className="home_center_container" style={{ backgroundColor: "rgba(255, 237, 105, 0.25)" }} onClick={handleOutsideClick}>
            <div className="play_options_container">
                <div className='play_option_container'>
                    <div className="play_option">
                        <div className="button_container">
                            <button
                                type="button"
                                className="button"
                                onClick={() => createRoom(false)}
                                onPointerEnter={() => playerHover()}
                            >
                                Create Room
                            </button>
                        </div>
                    </div>
                </div>
                <div className='play_option_container'>
                    {enterCode ?
                        <div className="play_option" onMouseLeave={() => setEnterCode(false)}>
                            <form className="enter_code_container" onSubmit={handleJoinRoom}>
                                <input
                                    type="text"
                                    placeholder='Enter Room Code...'
                                    value={roomName}
                                    className="roomname_container"
                                    onChange={(e) => setRoomName(e.target.value)}
                                />
                                <Send type="submit" className="enter_room_submit" onClick={() => joinRoom(roomName)} />
                            </form>
                        </div>
                        :
                        <div className="play_option" >
                            <div className="button_container">
                                <button
                                    type="button"
                                    className="button"
                                    onClick={() => setEnterCode(true)}
                                    onPointerEnter={() => playerHover()}
                                >
                                    Join Room
                                </button>
                            </div>
                        </div>
                    }
                </div>
                <div className='play_option_container'>
                    <div className="play_option">
                        <div className="button_container">
                            <button
                                type="button"
                                className="button"
                                onClick={() => createRoom(true)}
                                onPointerEnter={() => playerHover()}
                            >
                                Play VS Bots
                            </button>
                        </div>
                    </div>
                </div>

            </div>

        </div>
    );
};

interface RoomCodeProps {
    closeModal: (value: boolean) => void;
    joinRoom: (roomName: string) => void;
}

export const RoomCode: React.FC<RoomCodeProps> = ({ closeModal, joinRoom }) => {
    const [roomName, setRoomName] = useState("");

    function handleOutsideClick(event: React.MouseEvent<HTMLDivElement>) {
        if (event.target instanceof HTMLElement && event.target.id && event.target.id === "outside") {
            closeModal(false);
        }
    }

    function handleJoinRoom(e: React.FormEvent) {
        e.preventDefault();
        joinRoom(roomName);
    }

    return (
        <div id='outside' className="home_center_container" onClick={handleOutsideClick}>
            <form className="play_option" onSubmit={handleJoinRoom}>
                <p className="enter_code_text">Enter Room Code:</p>
                <input
                    type="text"
                    value={roomName}
                    className="roomname_container"
                    onChange={(e) => setRoomName(e.target.value)}
                />
                <div className="button_container">
                    <button className='button' onClick={() => joinRoom(roomName)}>Join Room</button>
                </div>
            </form>
        </div>
    );
};

